import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { userService } from "../../services/UserService";
import { CheckCircle, CircleSlash, Eye, EyeOff, Lock } from "lucide-react";
import { useLoader } from "../../context/LoaderContext";
import { useTheme } from "../../context/ThemeContext";
import { AppConfig } from "../../utils/Constants";

const PasswordResetComponent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { showLoader, hideLoader } = useLoader();
  const { theme } = useTheme();

  useEffect(() => {
    if (!token) {
      setError("Please use the reset button in the email.");
    }
  }, [token]);

  const handleResetPassword = async () => {
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFeedback(null);

      if (!token) throw new Error("No reset token provided.");
      showLoader();

      const response = await userService.passwordReset(token, newPassword);

      if (response && response.message) {
        setFeedback(response.message);
      } else {
        setFeedback("Password reset successfully! Redirecting to login...");
      }

      setTimeout(() => {
        navigate("/auth");
        hideLoader();
      }, 2000);
    } catch (error: any) {
      console.error("Error resetting password:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to reset the password.";
      setError(errorMessage);
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  const passwordsMatch = newPassword === confirmPassword && newPassword !== "";

  return (
    <div className="flex justify-center items-center min-h-screen bg-background-default dark:bg-background-dark px-6 relative overflow-hidden">
      {/* Subtle gradient orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/3 dark:bg-secondary/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/3 dark:bg-secondary/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10">
        {/* Card Container */}
        <div className="bg-background-paper dark:bg-background-paperDark rounded-2xl shadow-xl border border-divider dark:border-divider-dark p-8 backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary/10 dark:bg-secondary/20 mb-4">
              <Lock className="w-8 h-8 text-secondary" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-4">
              <img
                src={
                  theme === "light"
                    ? "https://cdn.forgeworks.ai/assets/forgeworks-logo-black.png"
                    : AppConfig.LOGO_URL
                }
                alt={`${AppConfig.PRODUCT_NAME_SHORT} Logo`}
                className="w-5 h-5 object-contain"
              />
              <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                {AppConfig.PRODUCT_NAME_SHORT}
              </span>
            </div>
            <h2 className="text-2xl font-semibold mb-2 tracking-tight text-text-primary dark:text-text-primaryDark">
              Reset Password
            </h2>
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
              Enter your new password below
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleResetPassword();
            }}
            className="space-y-5"
          >
            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 text-sm border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
                >
                  {showNewPassword ? (
                    <Eye className="w-4 h-4" />
                  ) : (
                    <EyeOff className="w-4 h-4" />
                  )}
                </button>
              </div>
              {newPassword && newPassword.length < 8 && (
                <p className="mt-1.5 text-xs text-text-secondary dark:text-text-secondaryDark">
                  Password must be at least 8 characters
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 text-sm border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
                  autoComplete="new-password"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {confirmPassword && (
                    <>
                      {passwordsMatch ? (
                        <CheckCircle className="w-4 h-4 text-green-dark dark:text-green-light" />
                      ) : (
                        <CircleSlash className="w-4 h-4 text-red-dark dark:text-red-light" />
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
                  >
                    {showConfirmPassword ? (
                      <Eye className="w-4 h-4" />
                    ) : (
                      <EyeOff className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={!passwordsMatch || loading || newPassword.length < 8}
              className={`w-full h-11 px-4 rounded-lg text-sm font-semibold transition ${
                !passwordsMatch || loading || newPassword.length < 8
                  ? "opacity-50 cursor-not-allowed bg-secondary"
                  : "bg-secondary hover:bg-secondary-hover"
              } text-white`}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-light dark:bg-red-dark/20 text-red-dark dark:text-red-light text-sm">
                <CircleSlash className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {feedback && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-light text-sm">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>{feedback}</span>
              </div>
            )}
          </form>

          <div className="mt-6 pt-6 border-t border-divider dark:border-divider-dark">
            <button
              onClick={() => navigate("/auth")}
              className="w-full py-2.5 rounded-lg text-sm font-medium border border-divider dark:border-divider-dark text-text-secondary dark:text-text-secondaryDark hover:bg-background-panel dark:hover:bg-background-panelDark transition-colors"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetComponent;
